import React, { ReactElement } from 'react';
import Controller, { OnCatch, OnClick, OnClose, OnMount, OnSuccess, OnUnmount } from '@philips-internal/oswald-ui-core/lib/client';
import { ButtonAPI, CarouselAPI, FooterAPI, ImageAPI, Link, ModalProps, ModalService, PanelAPI, RepeaterAPI, Text, TextAPI, ThemeService, ThemeTonalRange, ToastNotificationAPI } from '@philips-internal/oswald-ui-components';
import { LoginAPI, MultiFactorAuthenticationModalService } from '@philips-internal/oswald-ui-foundation';
import { ClickEvent, MountEvent, RequestEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { generateUUID, isMobile, isMobileResolution } from '@philips-internal/oswald-ui-utils';
import { Value } from '@philips-internal/oswald-ui-core/lib/types';
import { LayoutJustify, Size } from '@philips-internal/oswald-ui-constants';
import { Inject } from '@odinjs/odin';

import BaseController from '../BaseController';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import { ThemeLogin } from '../../interfaces/theme-login';
import { LandingPageItems } from '../../interfaces/landing-page-items';
import { APPLICATION_ROUTES } from '../../constants/application-routes';
import UserContext from '../../utils/user-context';
import { LoginUtils } from '../../utils/login-utils';
import { getFoundationDataContext } from '../../app/bootstrap-foundation';
import { FragmentNames, LoginLandingPageAliases } from '../../codegen';
import { HtmlViewer } from '@philips-emr/patient-components';

import './login-landing-page-controller.css';
import { mfaInputMessage } from '../../constants/mfa-messages-constants';
import { MfaLinkLongShortMessages } from '../../interfaces/security-authentication';

interface LinkCardData {
  href: string;
  title: string;
}

interface ConfigurationLogin {
  dsTitleLogin: string;
}

@Controller({ component: LoginLandingPageAliases.LOGIN_LANDING_PAGE_LAYOUT, fragment: FragmentNames.LOGIN_LANDING_PAGE })
class LoginLandingPageController extends BaseController {

  @Inject(LoginLandingPageAliases.LOGIN_LANDING_PAGE_ALERT_PANEL)
    loginLandingPageAlertPanelApi!: PanelAPI;

  @Inject(LoginLandingPageAliases.LOGIN_LANDING_PAGE_LINK_PANEL)
    loginLandingPageLinkPanelApi!: PanelAPI;

  @Inject(LoginLandingPageAliases.REPEATER)
    repeaterApi!: RepeaterAPI;

  @Inject(LoginLandingPageAliases.LOGIN_LANDING_PAGE_ALERT_TOAST_NOTIFICATION)
    loginLandingPageAlertToastNotificationApi!: ToastNotificationAPI;

  @Inject(LoginLandingPageAliases.LOGIN_LANDING_PAGE_INFO_CAROUSEL)
    loginLandingPageCarouselApi!: CarouselAPI;

  @Inject(LoginLandingPageAliases.NEW_ACCOUNT_PANEL)
    newAccountPanelApi!: PanelAPI;

  @Inject(LoginLandingPageAliases.LOGIN_LANDING_PAGE_ALERT_LOAD_MORE_BUTTON)
    loginLandingPageAlertLoadMoreButtonApi!: ButtonAPI;

  @Inject(LoginLandingPageAliases.LOGIN_LANDING_PAGE_LOGIN)
    loginApi!: LoginAPI;

  @Inject(LoginLandingPageAliases.LOGIN_LANDING_PAGE_LOGO_TEXT)
    loginLogoTextApi!: TextAPI;

  setSlidePages(slidePages: Record<string, Value>[]): void {
    let instCardsData = this.context.get('instCardsData') as LandingPageItems[];
    const title = instCardsData[0].dsTitulo as string;
    const pagination = isMobileResolution() ? 2 : 6;
    const isLastElementAlone = instCardsData.length % pagination === 1;
    const MIN_CARD_NUMBER = 2;
    let lastPage;

    instCardsData = instCardsData.filter(instCardData => instCardData.ieSituacao === 'A')

    if (instCardsData?.length >= MIN_CARD_NUMBER) {
      if (!isMobileResolution() && isLastElementAlone) {
        lastPage = this.getLastPage(instCardsData, title);
      }

      const numOfPages = Math.ceil(instCardsData.length / pagination)
      for (let page = 0; page < numOfPages; page++) {
        const startIndex = pagination * page;
        const finalIndex = startIndex + pagination > instCardsData.length ? instCardsData.length : startIndex + pagination;
        const infoCards = instCardsData.filter((_, index) => index >= startIndex && index < finalIndex)
          .map(info => ({ description: info.dsSubtituloItem, icon: info.dsInstitucional, title: info.dsTituloItem }))

        slidePages.push({
          'image': null,
          'infoCardsData': infoCards,
          'title': title
        })
      }

      if (lastPage) {
        slidePages.push(lastPage);
      }
    }

    let imageData = this.context.get('imageData') as LandingPageItems[];

    imageData = imageData.filter(image => image.ieSituacao === 'A')

    if (imageData && imageData.length > 0) {
      imageData.forEach(data => {
        if (data.dsAvatar) {
          slidePages.push({ 'image': data.dsAvatar });
        }
      })
    }
  }

  setAlertConfig(): void {
    let alertData = this.context.get('alertData') as LandingPageItems[];

    alertData = alertData.filter(alertDataElement => alertDataElement.ieSituacao === 'A');

    if (alertData.length > 0) {
      this.context.set({ alertData: alertData[0] });
      this.loginLandingPageAlertPanelApi.setVisible(true);
      this.loginLandingPageAlertToastNotificationApi.setVisible(true);
      this.loginLandingPageAlertToastNotificationApi.setTitle(alertData[0].dsTituloItem);
      this.loginLandingPageAlertToastNotificationApi.setMessage(alertData[0].dsSubtituloItem);

      if(!alertData[0].dsTextoItem) {
        this.loginLandingPageAlertLoadMoreButtonApi.setVisible(false);
      }

    } else {
      this.loginLandingPageAlertPanelApi.setVisible(false);
    }
  }

  setLinkConfig(): void {
    let linkData = this.context.get('linkData') as Record<string, Value>[];
    const MIN_LINK_NUMBER = 2;
    const MAX_LINK_NUMBER = 4;

    linkData = linkData.filter((link) => link.ieSituacao === 'A');

    if (linkData?.length < MIN_LINK_NUMBER) {
      this.loginLandingPageLinkPanelApi.setVisible(false);
    }

    if (linkData.length >= MIN_LINK_NUMBER && linkData.length <= MAX_LINK_NUMBER) {
      this.repeaterApi.setData(linkData);

      if (linkData.length === MAX_LINK_NUMBER) {
        this.loginLandingPageLinkPanelApi.setJustifyContent(LayoutJustify.SPACEBETWEEN);
      }
    }

    if (linkData.length > MAX_LINK_NUMBER) {
      this.repeaterApi.setData(linkData.splice(0, MAX_LINK_NUMBER));
    }
  }

  setLandingPageConfig(): void {
    const slidePages = [] as Record<string, Value>[];

    this.setSlidePages(slidePages);
    this.loginLandingPageCarouselApi.setData(slidePages);
    this.setAlertConfig();
    this.setLinkConfig();
  }

  getLastPage(infoCardData: LandingPageItems[], title: string): Record<string, Value> {
    const lastElement = infoCardData.pop();
    const penultElement = infoCardData.pop();
    const infoCards = [penultElement, lastElement]
      .map(info => ({ description: info?.dsSubtituloItem, icon: info?.dsAvatar, title: info?.dsTituloItem }))

    return {
      'image': null,
      'infoCardsData': infoCards,
      'title': title
    };
  }

  getImageLogo(): Promise<string> {
    return HttpService.get(APPLICATION_ENDPOINTS.GET_CONFIGURATION).then(response => {
      const { dsLogoLogin } = response.body as ThemeLogin;
      return dsLogoLogin;
    });
  }

  getTitleLogin(): Promise<string> {
    return HttpService.get(APPLICATION_ENDPOINTS.GET_CONFIGURATION).then(response => {
      const { dsTitleLogin } = response.body as ConfigurationLogin;
      return dsTitleLogin;
    });
  }

  checkHref(href: string): string {
    if (!href) return '';
    if (href.includes('https://') || href.includes('http://')) return href;

    return `//${href}`;
  }

  @OnMount({ target: LoginLandingPageAliases.LOGIN_LANDING_PAGE_LAYOUT })
  onMountLayout(): void {
    this.setLandingPageConfig();

    setTimeout(() => {
      const { loginContentTonalRange = ThemeTonalRange.ULTRA_LIGHT } = getFoundationDataContext().getBulkData();
      ThemeService.setContentTonalRange(loginContentTonalRange as ThemeTonalRange);
    });
  }

  @OnMount({ target: LoginLandingPageAliases.LOGIN_LANDING_PAGE_LOGIN })
  onMountLoginDesktop(event: MountEvent): void {
    const loginApi = event.target as LoginAPI;
    const defaultTitleLogin = MessageService.translate('patient.welcome.landing.page').concat(' ').concat(MessageService.translate('patient.title.landing.page'));
    this.getTitleLogin().then( titleLogin => {
      loginApi.setApplicationName( MessageService.translate(titleLogin || defaultTitleLogin ));
      this.loginLogoTextApi.setValue(loginApi.getApplicationName());
    });

    if (!isMobileResolution()) {
      this.getImageLogo().then(res => loginApi.setLogo(res));
    }

    this.setMfaLinkLongShortMessages();
    const multiFactorAuthenticationProps = loginApi.getMultiFactorAuthenticationProps();
    multiFactorAuthenticationProps.inputCodeExpression = mfaInputMessage();

    loginApi.setMultiFactorAuthenticationProps(multiFactorAuthenticationProps);
  }

  setMfaLinkLongShortMessages(): void {
    HttpService.get(APPLICATION_ENDPOINTS.GET_MFA_LINK_LONG_SHORT_MESSAGES).then(response => {
      const { linkMessage, longMessage, shortMessage } = response.body as MfaLinkLongShortMessages;

      const element = (
        <div>
          {shortMessage && shortMessage.trim() !== '' && (
            <Text value={shortMessage}/>
          )}
          {linkMessage && linkMessage.trim() !== '' && (
            <Link
              value={linkMessage}
              size={Size.LARGE}
              onClick={() => this.openAuthenticationModal(longMessage)}
            />
          )}
        </div>
      );
      MultiFactorAuthenticationModalService.setRecoveryContent(element);
    });
  }

  openAuthenticationModal(longMessage: string): void {
    ModalService.openDialog({
      closeable: true,
      contextData: { longMessage },
      fragment: FragmentNames.SECURITY_AUTHENTICATION_MODAL_LONG_DESCRIPTION,
      id: FragmentNames.SECURITY_AUTHENTICATION_MODAL_LONG_DESCRIPTION
    });
  }

  @OnMount({ target: LoginLandingPageAliases.FOOTER_FOOTER_LOGIN_LANDING_PAGE })
  onMountFooter(event: MountEvent): void {
    const footerAPI = event.target as FooterAPI;
    footerAPI.setTextExpression(
      MessageService.translate('tws.footer.text')
    );
  }

  @OnMount({ target: LoginLandingPageAliases.LOGIN_LANDING_PAGE_IMAGE_LOGO })
  onMountLogoMobile(event: MountEvent): void {
    const logoImageApi = event.target as ImageAPI;

    this.getImageLogo().then(res => logoImageApi.setSrc(res));
  }

  @OnMount({ target: LoginLandingPageAliases.NEW_ACCOUNT_PANEL })
  onNewAccountPanel(): void {
    if (!UserContext.twsUserParameters.hasAccessToUserRegistration) {
      this.newAccountPanelApi.setVisible(false);
    }
  }

  @OnUnmount({ target: LoginLandingPageAliases.LOGIN_LANDING_PAGE_LOGIN_PANEL })
  onUnmountLogin(): void {
    ThemeService.setContentTonalRange(ThemeTonalRange.ULTRA_LIGHT);
  }

  @OnClick({ target: LoginLandingPageAliases.LOGIN_LANDING_PAGE_SIGNIN_BUTTON })
  onClickSignIn(): void {
    document.querySelector('.login-landing-page-login-container')?.scrollIntoView();
  }

  @OnClick({ target: LoginLandingPageAliases.NEW_ACCOUNT_BUTTON })
  onClickNewAccountButton(): void {
    this.historyService.push(APPLICATION_ROUTES.ACCOUNT_CREATION_MODAL, {});
  }

  @OnClick({ target: LoginLandingPageAliases.LOGIN_LANDING_PAGE_ALERT_LOAD_MORE_BUTTON })
  onClickLoadMore(): void {
    const alertData = this.context.get('alertData') as LandingPageItems;

    const modalProps: ModalProps = {
      children: this.getHtmlViewer(alertData.dsTextoItem),
      className: 'login-landing-page__modal-covid',
      closeable: true,
      closeByDocument: true,
      closeByEscape: true,
      focusOnOpen: true,
      onClose: () => { return },
      theme: ThemeTonalRange.ULTRA_LIGHT,
      title: alertData.dsTituloItem,
      uuid: generateUUID(),
    }

    if (isMobile()) {
      ModalService.openFullscreen(modalProps);
    } else {
      ModalService.openDialog(modalProps);
    }
  }

  private getHtmlViewer(dsTextoItem: string): ReactElement {
    return <HtmlViewer htmlContent={dsTextoItem} />;
  }

  @OnClick({ target: LoginLandingPageAliases.LOGIN_LANDING_PAGE_LINK_CARD })
  onClickLinkCard(event: ClickEvent): void {
    const cardData = event.value as LinkCardData;
    const url = this.checkHref(cardData.href);
    const windowReference = window.open();

    if (windowReference) {
      windowReference.location = url;
    }
  }

  @OnClose({ target: LoginLandingPageAliases.LOGIN_LANDING_PAGE_ALERT_TOAST_NOTIFICATION })
  onClose(): void {
    this.loginLandingPageAlertPanelApi.setVisible(false);
  }

  @OnCatch({ target: LoginLandingPageAliases.LOGIN_LANDING_PAGE_LOGIN })
  onCatch(event: RequestEvent<Error | Record<string, string>>): void {
    LoginUtils.handleLoginError(event);
  }

  @OnSuccess(LoginLandingPageAliases.LOGIN_LANDING_PAGE_LOGIN)
  onSuccess(): void {
    LoginUtils.loginSucess();
  }

}

export default LoginLandingPageController;
